import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import apiFetch, { ensureStatusOk } from '../util/fetch-utils';
import styled from "styled-components";
import * as d3 from "d3";
import { toaster } from 'evergreen-ui';

import {
    COMPLETED,
    ERROR_404,
    IN_PROGRESS,
    CURRENT_MONITORED_SELECT_SNAPSHOT,
    CURRENT_MONITORED_URI_CHANGED,
    CURRENT_MONITORED_URI_CHANGES_FETCHED,
    CURRENT_MONITORED_URI_FETCHED,
    CURRENT_MONITORED_URI_PERFORMANCES_FETCHED,
    CURRENT_MONITORED_URI_SEARCH_PERFORMANCES_FETCHED,
    CURRENT_MONITORED_URI_SNAPSHOT_FETCH_404,
    CURRENT_MONITORED_URI_SNAPSHOT_FETCH_FAILED,
    CURRENT_MONITORED_URI_SNAPSHOT_FETCH_STARTED,
    CURRENT_MONITORED_URI_SNAPSHOT_FETCH_SUCCESS,
} from "../../reducers/currentMonitoredUri";

import { extractDestinationUri } from "./commons";
import { trafficPathOf } from "../common/trafficPath";
import Header from "./Header";
import Tabs from "./Tabs";
import Timeline from "./Timeline";
import Buttons from "./Buttons";
import Drawer from "./Drawer";

export const SOURCE_PERF_TAG = "src";
export const DESTINATION_PERF_TAG = "dst";

const SNAPSHOT_DEFAULTS = {
    fetchStatus: COMPLETED,
    title: "",
    metaDescriptions: [],
    h1s: [],
    h2s: [],
    canonicals: [],
    destinationUri: "",
};

function formatDate(date, fallback) {
    if (date === undefined) return d3.timeFormat('%d %b %Y')(Date.parse(fallback));
    return d3.timeFormat('%d %b %Y – %H:%M')(Date.parse(date));
}

function getInternalAhrefLinksCount(lastCrawl) {
    const c = lastCrawl?.inboundLinksCount;
    if (!c?.internal) return "-";
    return c.internal.ahref;
}

export default function CurrentMonitoredUri() {
    const dispatch = useDispatch();
    const params = useParams();
    const location = useLocation();

    const id = params.id;
    const workspaceNumber = params.workspaceNumber;

    const {
        uri,
        curSnapshotDate,
        lastSnapshotDate,
        pageSnapshotsMap,
        pageChanges,
        recommendation,
        numDaysHistory,
    } = useSelector(s => s.currentMonitoredUri);

    const workspace = useSelector(s => s.workspaces.data.find(ws => String(ws.seqNumber) === workspaceNumber));
    const [fetched, setFetched] = useState(null);

    const [tab, setTab] = useState('details');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerPinned, setDrawerPinned] = useState(false);

    // If the active tab is 'changes' but the selected snapshot has no changes, fall back to details.
    useEffect(() => {
        if (tab === 'changes' && Array.isArray(pageChanges) && !pageChanges.some(
            pc => pc.changes?.length > 0 && pc.crawlDate?.startsWith(curSnapshotDate)
        )) {
            setTab('details');
        }
    }, [curSnapshotDate, pageChanges]);

    // Initial fetch — also resets tab to Details when navigating to a different URI
    useEffect(() => {
        setTab('details');
        dispatch({ type: CURRENT_MONITORED_URI_CHANGED });
        apiFetch('/api/monitored-uris/' + id + '?projection=InlineCurValue')
            .then(r => {
                if (r.status !== 200) return;
                r.json().then(data => {
                    const dstUri = extractDestinationUri(data.currentValue);
                    if (data.currentValue) data.currentValue.destinationUri = dstUri;
                    dispatch({ type: CURRENT_MONITORED_URI_FETCHED, payload: data });
                    setFetched(data);
                    fetchPageChanges(data.uri);
                });
            });
    }, [id]);

    // Waits for the workspace too: which URL owns a path's traffic depends on its Search Console property.
    useEffect(() => {
        if (fetched && workspace) fetchPerformances(fetched, workspace);
    }, [fetched, workspace]);


    // Reads ?date= from URL and keeps curSnapshotDate in sync.
    // Waits for lastSnapshotDate (set on initial load) so the URL param always wins over the reducer's default.
    useEffect(() => {
        if (!lastSnapshotDate) return;
        const dateParam = new URLSearchParams(location.search).get('date');
        const target = dateParam || lastSnapshotDate;
        if (target !== curSnapshotDate) {
            dispatch({ type: CURRENT_MONITORED_SELECT_SNAPSHOT, curSnapshotDate: target });
        }
    }, [lastSnapshotDate, location.search]);
    // Fetch snapshot when selected date changes and snapshot isn't cached
    useEffect(() => {
        if (!curSnapshotDate || !uri) return;
        const snap = pageSnapshotsMap[curSnapshotDate];
        if (snap?.fetchStatus) return;

        dispatch({ type: CURRENT_MONITORED_URI_SNAPSHOT_FETCH_STARTED, curSnapshotDate });

        apiFetch('/archive-api/page?' + new URLSearchParams({ uri, workspaceNumber, date: curSnapshotDate }))
            .then(r => {
                if (r.status === 404) {
                    dispatch({ type: CURRENT_MONITORED_URI_SNAPSHOT_FETCH_404, curSnapshotDate });
                    return;
                }
                if (r.status !== 200) throw new Error('Unable to fetch the crawl of ' + curSnapshotDate + '. ' + r.status + ' ' + r.statusText);
                return r.json().then(data => {
                    data.destinationUri = extractDestinationUri(data);
                    dispatch({ type: CURRENT_MONITORED_URI_SNAPSHOT_FETCH_SUCCESS, curSnapshotDate, value: data });
                });
            })
            .catch(err => {
                console.error(err.message);
                toaster.danger(err.message);
                dispatch({ type: CURRENT_MONITORED_URI_SNAPSHOT_FETCH_FAILED, curSnapshotDate });
            });
    }, [curSnapshotDate, uri, workspaceNumber]);
    function fetchPageChanges(pageUri) {
        apiFetch('/archive-api/page-changes?uri=' + pageUri + '&workspaceNumber=' + workspaceNumber)
            .then(r => {
                if (!r.ok) throw new Error('Unable to fetch page changes. ' + r.status + ' ' + r.statusText);
                return r.json();
            })
            .then(crawls => dispatch({ type: CURRENT_MONITORED_URI_CHANGES_FETCHED, payload: crawls }))
            .catch(err => { console.error(err.message); toaster.danger(err.message); });
    }

    // The path carries its query string, because a paginated or filtered URL is its own monitoredUri
    // and earns its own traffic: /it/13-fedi-sarde-oro?page=2 is a different page from
    // /it/13-fedi-sarde-oro, and pagePerformances holds them apart.
    function fetchPerformances(data, workspace) {
        const srcPath = trafficPathOf(data.uri, workspace);
        const ws = data.workspaceNumber || workspaceNumber;
        if (srcPath !== null) fetchPerformanceFor(ws, srcPath, SOURCE_PERF_TAG);
        fetchSearchPerformanceFor(ws, data.uri);
        const dstUri = data.currentValue?.destinationUri;
        if (dstUri && dstUri !== data.uri) {
            try {
                const dstPath = trafficPathOf(dstUri, workspace);
                if (dstPath !== null) fetchPerformanceFor(ws, dstPath, DESTINATION_PERF_TAG);
            } catch (e) { console.error("Unable to fetch:", e); }
        }
    }

    function fetchPerformanceFor(ws, path, tag) {
        apiFetch('/api/google/page-performances?workspaceNumber=' + ws + '&path=' + encodeURIComponent(path) + '&days=' + numDaysHistory)
            .then(ensureStatusOk)
            .then(r => r.json())
            .then(data => dispatch({ type: CURRENT_MONITORED_URI_PERFORMANCES_FETCHED, payload: { tag, path, data } }))
            .catch(err => { console.error(err.message); toaster.danger(err.message); });
    }

    function fetchSearchPerformanceFor(ws, uri) {
        apiFetch('/api/google/page-search-performances?workspaceNumber=' + ws + '&uri=' + encodeURIComponent(uri) + '&days=' + numDaysHistory)
            .then(ensureStatusOk)
            .then(r => r.json())
            .then(data => dispatch({ type: CURRENT_MONITORED_URI_SEARCH_PERFORMANCES_FETCHED, payload: data }))
            .catch(err => { console.error(err.message); toaster.danger(err.message); });
    }

    const snap = pageSnapshotsMap[curSnapshotDate];
    let cur;
    if (snap?.fetchStatus === ERROR_404) {
        cur = { ...SNAPSHOT_DEFAULTS, fetchStatus: ERROR_404 };
    } else if (!snap || snap.fetchStatus !== COMPLETED) {
        // The latest crawl stands in while the selected one loads, so the form doesn't flash empty.
        const lastSnap = pageSnapshotsMap[lastSnapshotDate];
        const placeholder = lastSnap?.fetchStatus === COMPLETED ? lastSnap : SNAPSHOT_DEFAULTS;
        cur = { ...placeholder, fetchStatus: snap?.fetchStatus || IN_PROGRESS };
    } else {
        cur = snap;
    }

    const values = { cur, ref: recommendation };
    const isLoading = cur.fetchStatus === IN_PROGRESS;
    const internalAhref = getInternalAhrefLinksCount(cur.lastCrawl);
    const crawlDate = formatDate(cur.createDate, curSnapshotDate);
    const changesCount = Array.isArray(pageChanges)
        ? (pageChanges.find(pc => pc.crawlDate?.startsWith(curSnapshotDate))?.changes?.length || 0)
        : 0;
    const hasChanges = changesCount > 0;

    return (
        <Wrap>
            <MainArea>
                <Header uri={uri} isLoading={isLoading} />
                <TimelineCard>
                    <Timeline onOpenDrawer={() => setDrawerOpen(true)} />
                    <NavDivider />
                    <NavRow><Buttons /></NavRow>
                </TimelineCard>
                <Tabs
                    values={values}
                    uri={uri}
                    internalAhref={internalAhref}
                    hasChanges={hasChanges}
                    changesCount={changesCount}
                    tab={tab}
                    onTabChange={setTab}
                />
            </MainArea>
            <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                pinned={drawerPinned}
                onPinChange={setDrawerPinned}
            />
        </Wrap>
    );
}

const Wrap = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 16px;
`;

const MainArea = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
    min-width: 0;
`;

const TimelineCard = styled.div`
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    @media (max-width: 600px) { border-radius: 0; border-left: none; border-right: none; }
`;

const NavDivider = styled.div`
    border-top: 1px solid #e2e8f0;
`;

const NavRow = styled.div`
    display: flex;
    justify-content: center;
    padding: 10px 0;
`;
